import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Chip, Icon, IconButton, Menu, useToast } from '@w7/ui'
import { useCmMe } from '@/api/hooks/useCmMe'
import { NAV, activeNavGroupId, navLinkEnd } from './nav'
import { canAccessUiPath, filterNavByGrants } from './staffAccess'
import { useThemePrefs } from './theme'
import { StrixMark } from '@/brand/StrixMark'
import { decodePreferredUsername, logout as oidcLogout } from '@/auth/oidc'
import { readAuth } from '@/auth/session'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'

type MiniNavTip = { label: string; description: string; top: number; left: number }

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  /** Accordion: at most one group open (null until path resolves). */
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)
  const [miniTip, setMiniTip] = useState<MiniNavTip | null>(null)
  const me = useCmMe()
  const nav = useMemo(() => filterNavByGrants(NAV, me.data?.caller), [me.data?.caller])
  const navPaths = useMemo(() => nav.flatMap((g) => g.items.map((i) => i.to)), [nav])
  const activeGroupId = useMemo(
    () => activeNavGroupId(nav, location.pathname, navPaths),
    [nav, location.pathname, navPaths],
  )

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
    if (!collapsed) setMiniTip(null)
  }, [collapsed])

  useEffect(() => {
    setRailOpen(false)
    setMiniTip(null)
  }, [location.pathname])

  // Open the section for the current page (closes any other).
  useEffect(() => {
    if (activeGroupId) setOpenGroupId(activeGroupId)
  }, [activeGroupId])

  const toggleGroup = (id: string) => {
    setOpenGroupId((prev) => (prev === id ? null : id))
  }

  return (
    <div className="w7-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="CM navigation"
      >
        <div className="w7-rail-head">
          <Link to="/home" className="strix-mark-link" aria-label="Company Master — home">
            <StrixMark
              size={collapsed ? 28 : 26}
              showName={!collapsed}
              suffix={collapsed ? undefined : 'CM console'}
            />
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={() => setCollapsed((value) => !value)}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll" aria-label="Console sections">
          {nav.map((group) => {
            const open = openGroupId === group.id
            const leadIcon = group.icon ?? group.items[0]?.icon ?? 'overview'
            const showMiniTip = (item: (typeof group.items)[number], el: HTMLElement) => {
              if (!collapsed) return
              const box = el.getBoundingClientRect()
              setMiniTip({
                label: group.items.length === 1 ? group.label : item.label,
                description: item.description,
                top: box.top + box.height / 2,
                left: box.right + 10,
              })
            }

            // Single-item sections (e.g. Commission) — one row, no accordion.
            if (group.items.length === 1) {
              const item = group.items[0]!
              return (
                <div key={group.id} className="w7-navgroup" data-solo="true">
                  <NavLink
                    to={item.to}
                    end={navLinkEnd(item.to, navPaths)}
                    className="w7-navitem w7-navgroup-solo"
                    title={
                      collapsed ? `${group.label} — ${item.description}` : item.description
                    }
                    aria-label={collapsed ? group.label : undefined}
                    onMouseEnter={(event) => showMiniTip(item, event.currentTarget)}
                    onMouseLeave={() => setMiniTip(null)}
                  >
                    <span className="w7-navitem-icon" aria-hidden="true">
                      <Icon name={item.icon} size={18} />
                    </span>
                    <span className="w7-navitem-label">{group.label}</span>
                  </NavLink>
                </div>
              )
            }

            return (
              <div
                key={group.id}
                className="w7-navgroup"
                data-open={open ? 'true' : 'false'}
              >
                <button
                  type="button"
                  className="w7-navgroup-toggle"
                  aria-expanded={open}
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="w7-navgroup-toggle-icon" aria-hidden="true">
                    <Icon name={leadIcon} size={18} />
                  </span>
                  <span className="w7-navgroup-toggle-label">{group.label}</span>
                  <span className="w7-navgroup-toggle-chevron" aria-hidden="true">
                    <Icon name="chevron-down" size={14} />
                  </span>
                </button>
                <div className="w7-navgroup-items">
                  <div className="w7-navgroup-items-inner">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.id}
                        to={item.to}
                        end={navLinkEnd(item.to, navPaths)}
                        className="w7-navitem"
                        title={collapsed ? `${item.label} — ${item.description}` : item.description}
                        aria-label={collapsed ? item.label : undefined}
                        onMouseEnter={(event) => showMiniTip(item, event.currentTarget)}
                        onMouseLeave={() => setMiniTip(null)}
                      >
                        <span className="w7-navitem-icon" aria-hidden="true">
                          <Icon name={item.icon} size={18} />
                        </span>
                        <span className="w7-navitem-label">{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </nav>

        <div
          className="w7-rail-foot"
          title="Signed in via Keycloak — CM-API calls use your bearer token."
        >
          <Chip tone="profit" size="sm" icon="live" className="w7-rail-session">
            Live session
          </Chip>
          <p className="w7-rail-session-hint w7-dim">
            Catalog ops go through CM-API with your Keycloak token.
          </p>
        </div>
      </aside>

      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      {collapsed && miniTip ? (
        <div
          className="w7-navitem-flyout"
          role="tooltip"
          style={{ top: miniTip.top, left: miniTip.left }}
        >
          <span className="w7-navitem-flyout-title">{miniTip.label}</span>
          <span className="w7-navitem-flyout-desc">{miniTip.description}</span>
        </div>
      ) : null}

      <div className="w7-main">
        <Topbar onOpenRail={() => setRailOpen(true)} />
        <main className="w7-content" id="main">
          {children}
        </main>
      </div>
    </div>
  )
}

function Topbar({ onOpenRail }: { onOpenRail: () => void }) {
  const toast = useToast()
  const navigate = useNavigate()
  const me = useCmMe()
  const { openPreferences } = useThemePrefs()
  const auth = readAuth()
  const signedInAs = decodePreferredUsername(auth?.accessToken ?? null) ?? 'Company Master'
  const showChangePassword = canAccessUiPath(me.data?.caller, '/change-password')

  const logout = () => {
    toast.success('Logged out')
    oidcLogout()
  }

  return (
    <header className="w7-topbar">
      <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={onOpenRail} />

      <span className="w7-spacer" />

      <div className="w7-topbar-trail">
        <Menu
          label="Account"
          align="end"
          trigger={<Avatar name={signedInAs} />}
          header={
            <div className="w7-menu-who">
              <Avatar name={signedInAs} size="sm" />
              <div className="w7-menu-who-text">
                <span className="w7-menu-who-name w7-truncate" title={signedInAs}>
                  {signedInAs}
                </span>
                <span className="w7-menu-who-role">Company Master</span>
              </div>
            </div>
          }
          items={[
            {
              id: 'prefs',
              label: 'Appearance',
              icon: 'sliders',
              onSelect: () => openPreferences(),
            },
            ...(showChangePassword
              ? [
                  {
                    id: 'change-password',
                    label: 'Change Password',
                    icon: 'lock' as const,
                    onSelect: () => navigate('/change-password'),
                  },
                ]
              : []),
            'separator',
            {
              id: 'out',
              label: 'Sign out',
              icon: 'logout',
              tone: 'danger',
              onSelect: logout,
            },
          ]}
        />
      </div>
    </header>
  )
}
